import { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { filter, get, map, includes, uniqBy } from "lodash";
import propertySetStyles from "../../shared/propertySets/propertySets.module.css";
import PropertyShow from "../../appCreator/properties/PropertyShow";
import Sorter from "../../shared/tables/Sorter";
import { usePropertySet } from "../../shared/propertySets/api";
import {
  columnCellWrapperClasses,
  columnHeaderClasses,
} from "../../shared/propertySets/tableColumnClasses";

const isSortable = (property) =>
  includes(["date", "text", "selection"], property.type);

// Hook to build extra columns for file profile property set
// identified by given contextId and contextType
const useExtraColumns = ({ contextId, contextType }) => {
  const { isLoading: fileLoading, data: fileProfilePropertySet } =
    usePropertySet({
      name: "file_profile",
      contextId,
      contextType,
    });
  const { isLoading: folderLoading, data: folderProfilePropertySet } =
    usePropertySet({
      name: "folder_profile",
      contextId,
      contextType,
    });

  return useMemo(() => {
    const properties = (get(fileProfilePropertySet, "properties") || []).concat(
      get(folderProfilePropertySet, "properties"),
    );
    // Columns come from the per-property `show_in_list` flag, maintained by
    // the network admin in the property-set builder — same mechanism as the
    // contact list (tixxt/core#216). The former SAD config
    // `file_profiles.column_fields` is gone; ordering now follows the field
    // order of the profile instead of the order of that config list.
    //
    // Deduplicated by name: this feeds the MIXED table, where file and folder
    // rows share the columns. A field of the same name in both profiles is one
    // shared column — file rows fill it from the file profile, folder rows from
    // the folder profile. Without this, such a field would render twice with
    // identical content (the old column_fields config matched by name and so
    // could never produce a duplicate).
    const columnProperties = uniqBy(filter(properties, "show_in_list"), "name");

    const extraColumns = map(columnProperties, (property) => {
      function Header({ handleSort, sortBy }) {
        return (
          // shared type→width mapping (docs/frontend_guide.md, "Tabellen")
          <th
            className={classNames(
              `col-profile-${property.name}`,
              columnHeaderClasses(property),
            )}
          >
            <Sorter
              attribute={`profile.${property.name}`}
              label={property.label}
              handleSort={handleSort}
              sortBy={sortBy}
              disabled={!isSortable(property)}
            />
          </th>
        );
      }
      function Data({ profile }) {
        return (
          <td
            className={classNames(propertySetStyles.value, "align-top", {
              "text-right": property.type === "number",
              "whitespace-nowrap":
                property.type === "number" || property.type === "date",
            })}
          >
            {profile ? (
              <div className={columnCellWrapperClasses(property)}>
                <PropertyShow
                  item={{ values: profile }}
                  property={property}
                  options={{ truncate: true }}
                />
              </div>
            ) : null}
          </td>
        );
      }
      Data.propTypes = { profile: PropTypes.object };
      return { Header, Data };
    });

    return { extraColumns, loading: folderLoading || fileLoading };
  }, [
    fileProfilePropertySet,
    folderProfilePropertySet,
    folderLoading,
    fileLoading,
  ]);
};

export default useExtraColumns;
